import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { jordanDate, ROUTES, type VaultDate } from '@serb/shared';
import { api } from '../../api/client';
import { KEYS } from '../../api/keys';
import { useNotice } from '../../components/NoticeProvider';
import { SAMPLE_DATA } from '../../lib/sample';
import { useAction } from '../../lib/useAction';
import { clockApi, useClock } from '../clock/useClock';
import { fromAlarm, fromVaultDate, sampleReminders, type ReminderItem } from './model';

/**
 * Reminders you set (kept on the server), the dates coming up in the Serb Mind vault (next steps, sailings, quotations
 * running out, invoices falling due), and sample ones while sample data is on. Oldest first.
 */
export function useReminders() {
  const qc = useQueryClient();
  const clock = useClock();
  const notice = useNotice();
  const today = jordanDate();
  const [sample, setSample] = useState<ReminderItem[]>(() => (SAMPLE_DATA ? sampleReminders(today) : []));
  const vault = useQuery({
    queryKey: KEYS.vaultDates,
    queryFn: () => api.get<VaultDate[]>(ROUTES.api.vaultDates),
    enabled: !SAMPLE_DATA,
  });
  const remove = useAction(clockApi.remove, { success: 'Done. The reminder is cleared.' });

  const items = [
    ...(clock.data?.pending ?? []).map(fromAlarm),
    ...(vault.data ?? []).map(fromVaultDate),
    ...sample,
  ].sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));

  // a date from the vault: it is hidden here at once and Claude is told, so it fixes the vault and the date does not come back
  const markVaultDone = useAction(
    async (r: ReminderItem) => {
      await api.post(ROUTES.api.vaultDateDone, { id: r.id.replace(/^vault-/, ''), date: r.date });
      await qc.invalidateQueries({ queryKey: KEYS.vaultDates });
    },
    { success: 'Marked done. Claude is updating the vault so it does not come back.' },
  );
  const complete = (r: ReminderItem) => {
    if (r.source === 'vault') return markVaultDone.run(r);
    if (r.source === 'real') return remove.run(r.id);
    setSample((all) => all.map((x) => (x.id === r.id ? { ...x, complete: true } : x)));
    notice('Marked as complete (sample reminder).', 'success');
  };
  const del = (r: ReminderItem) => {
    if (r.source === 'vault') return markVaultDone.run(r);
    if (r.source === 'real') return remove.run(r.id);
    setSample((all) => all.filter((x) => x.id !== r.id));
    notice('Deleted (sample reminder).', 'success');
  };
  return { items, today, loading: clock.isPending, busy: remove.busy || markVaultDone.busy, complete, del };
}
